import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import FilterBar from '@/components/feature/FilterBar';
import { videos } from '@/mocks/videos';
import { filterVideos, hotKeywords, searchVideos, sortVideos, type SortValue } from '@/utils/video';

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParam = searchParams.get('q') ?? '';

  const [input, setInput] = useState(queryParam);
  const [region, setRegion] = useState('全部');
  const [year, setYear] = useState('全部');
  const [sort, setSort] = useState<SortValue>('latest');

  useEffect(() => {
    setInput(queryParam);
  }, [queryParam]);

  const results = useMemo(() => {
    const matched = searchVideos(videos, queryParam);
    const filtered = filterVideos(matched, { region, year });
    return sortVideos(filtered, sort);
  }, [queryParam, region, year, sort]);

  const runSearch = (keyword: string) => {
    const kw = keyword.trim();
    setSearchParams(kw ? { q: kw } : {});
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />
      <main className="flex-1">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-5 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-900 font-medium">搜索</span>
          </nav>

          <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8 mb-6">
            <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">
              搜索视频
            </h1>
            <p className="mt-2 text-sm text-foreground-500">
              输入关键词，按回车即可搜索电影、电视剧、综艺与短视频内容。
            </p>
            <div className="flex items-center gap-2 mt-5 bg-background-100 rounded-md px-3 h-12 max-w-2xl">
              <i className="ri-search-line text-foreground-500 text-lg"></i>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') runSearch(input);
                }}
                placeholder="如：科幻、悬疑、美食、治愈"
                className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
              />
              <button
                type="button"
                onClick={() => runSearch(input)}
                className="px-5 h-9 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <span className="text-sm text-foreground-500">热门关键词：</span>
              {hotKeywords.map((kw) => (
                <button
                  key={kw}
                  type="button"
                  onClick={() => runSearch(kw)}
                  className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
                >
                  {kw}
                </button>
              ))}
            </div>
          </div>

          {queryParam && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <p className="text-sm text-foreground-600">
                  关键词 <strong className="text-foreground-900">“{queryParam}”</strong> 共找到{' '}
                  <strong className="text-primary-600">{results.length}</strong> 条结果
                </p>
              </div>

              <div className="mb-6">
                <FilterBar
                  region={region}
                  year={year}
                  sort={sort}
                  onRegionChange={setRegion}
                  onYearChange={setYear}
                  onSortChange={setSort}
                />
              </div>
            </>
          )}

          {queryParam && results.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {results.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          )}

          {queryParam && results.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
                <i className="ri-search-eye-line text-3xl"></i>
              </span>
              <h2 className="font-heading text-lg font-bold text-foreground-950">没有找到相关内容</h2>
              <p className="mt-2 text-sm text-foreground-500 max-w-md">
                换一个关键词试试，或从下方热门关键词中发现感兴趣的内容。
              </p>
              <div className="flex flex-wrap justify-center gap-2 mt-5 max-w-lg">
                {hotKeywords.map((kw) => (
                  <button
                    key={kw}
                    type="button"
                    onClick={() => runSearch(kw)}
                    className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
                  >
                    {kw}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!queryParam && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
                <i className="ri-search-line text-3xl"></i>
              </span>
              <h2 className="font-heading text-lg font-bold text-foreground-950">开始你的发现之旅</h2>
              <p className="mt-2 text-sm text-foreground-500">在上方输入关键词，或点击热门关键词快速搜索。</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}